import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { ErrorMessage } from '~/components/form-fields/ErrorMessage';
import { Label } from '~/components/form-fields/Label';
import type { FormInputProps } from '../FormProps';

type Props = FormInputProps & {
	mustIncludeCountryCode?: boolean;
};

export const PhoneInput = ({
	id,
	name,
	labelText,
	placeholder,
	autoFocus,
	autoComplete = 'off',
	required,
	control,
	disabled,
	errorMessage,
	mustIncludeCountryCode = false,
}: Props) => {
	const selectedTextRef = useRef<string>('');

	return (
		<div>
			{labelText && <Label id={id} labelText={labelText} />}
			<div className="mt-1 relative border border-gray-300 rounded-md shadow-xs">
				<Controller
					control={control}
					name={name}
					render={({ field: { onChange, name, value } }) => {
						const phoneLength = value ? value.replace(/[^0-9]+/g, '').length : 0;

						const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
							const target = e.target as HTMLInputElement;
							const start = target.selectionStart ?? 0;
							const end = target.selectionEnd ?? 0;
							const selectedText = target.value.substring(start, end);
							selectedTextRef.current = selectedText.replace(/[^0-9]+/g, '');
						};

						const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
							if (mustIncludeCountryCode) {
								// Currently hardcoded for +1 country code since we don't support other country codes in the format anyways
								// Also U.S. area codes never start with 1
								const inputDigitsOnly = e.target.value.replace(/[^0-9]+/g, '');
								const valueDigitsOnly = value?.replace(/[^0-9]+/g, '');
								// Check if the entire previous value was selected (comparing digits only)
								const hadFullSelection = selectedTextRef.current === valueDigitsOnly;

								// If empty input and user is typing, start with 1
								if (!valueDigitsOnly && inputDigitsOnly[0] !== '1') {
									e.target.value = '1' + inputDigitsOnly;
								}
								// If user had all text selected and is overwriting it
								else if (hadFullSelection && inputDigitsOnly.length > 0 && inputDigitsOnly[0] !== '1') {
									// Reset selection tracking
									selectedTextRef.current = '';
									e.target.value = '1' + inputDigitsOnly;
								}
							}
							onChange(e);
						};

						return (
							<PatternFormat
								autoComplete={autoComplete}
								autoFocus={autoFocus}
								disabled={disabled}
								className="focus:ring-blue-500 focus:border-blue-500 block w-full border-gray-300 rounded-md px-3 py-2 placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
								format={!mustIncludeCountryCode && phoneLength <= 10 ? '(###) ###-####' : '+# (###) ###-####'}
								id={id}
								name={name}
								onChange={handleChange}
								onSelect={handleSelect}
								placeholder={placeholder}
								required={required}
								type="text"
								value={value}
							/>
						);
					}}
				/>
			</div>
			{errorMessage && <ErrorMessage errorMessage={errorMessage} />}
		</div>
	);
};
